import React, { useState } from 'react';
import type { User } from 'firebase/auth';
import { Volume2, Lock, LogOut, Bell, ChevronRight, ShieldBan } from 'lucide-react';
import { useTimerStore, signOutUser, signInWithGoogle } from '@flowfocus/core';
import { Switch, Stepper, Segmented, SectionLabel, Sheet } from '../components/ui';
import { WhiteNoisePanel } from '../components/WhiteNoisePanel';
import { requestNotifications, notificationPermission } from '../lib/notify';
import { useExtensionConnected } from '../lib/blockerBridge';

export function SettingsPage({ user }: { uid: string; user: User }) {
  const { settings, updateSettings } = useTimerStore();
  const [showNoise, setShowNoise] = useState(false);
  const [notifPerm, setNotifPerm] = useState(notificationPermission());
  const extConnected = useExtensionConnected();
  const set = updateSettings;

  const enableNotifs = async () => setNotifPerm(await requestNotifications());

  return (
    <div style={{ height: '100%', overflowY: 'auto' }}>
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '22px 22px 90px' }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', margin: '0 0 4px' }}>Ajustes</h1>

        {/* Account */}
        <div className="card" style={{ borderRadius: 14, marginTop: 16, padding: 16, display: 'flex', alignItems: 'center', gap: 13 }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--primary)', color: 'var(--on-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 17 }}>
            {user.isAnonymous ? '·' : (user.displayName ?? user.email ?? 'U').slice(0, 1).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.isAnonymous ? 'Invitado' : (user.displayName ?? 'Cuenta')}</div>
            <div style={{ fontSize: 13, color: 'var(--text-mut)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.isAnonymous ? 'Sin cuenta · solo este dispositivo' : user.email}</div>
          </div>
          {user.isAnonymous
            ? <button className="btn-primary" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => signInWithGoogle()}>Vincular Google</button>
            : <button className="icon-btn" onClick={signOutUser} title="Cerrar sesión"><LogOut size={17} color="var(--danger)" /></button>}
        </div>

        <SectionLabel>Temporizador</SectionLabel>
        <div className="card" style={{ borderRadius: 13, overflow: 'hidden' }}>
          <Row label="Enfoque (min)" first><Stepper value={settings.workDuration} min={1} max={90} onChange={(v) => set({ workDuration: v })} /></Row>
          <Row label="Descanso corto (min)"><Stepper value={settings.shortBreakDuration} min={1} max={30} onChange={(v) => set({ shortBreakDuration: v })} /></Row>
          <Row label="Descanso largo (min)"><Stepper value={settings.longBreakDuration} min={1} max={60} onChange={(v) => set({ longBreakDuration: v })} /></Row>
          <Row label="Pomodoros entre descanso largo"><Stepper value={settings.longBreakAfter} min={2} max={8} onChange={(v) => set({ longBreakAfter: v })} /></Row>
          <Row label="Objetivo diario (pomodoros)"><Stepper value={settings.dailyGoal} min={1} max={20} onChange={(v) => set({ dailyGoal: v })} /></Row>
        </div>

        <SectionLabel>Comportamiento</SectionLabel>
        <div className="card" style={{ borderRadius: 13, overflow: 'hidden' }}>
          <Row label="Auto-iniciar descansos" first><Switch on={settings.autoStartBreaks} onChange={(v) => set({ autoStartBreaks: v })} /></Row>
          <Row label="Auto-iniciar pomodoros"><Switch on={settings.autoStartPomodoros} onChange={(v) => set({ autoStartPomodoros: v })} /></Row>
          <Row label={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Lock size={15} color="var(--text-mut)" /> Modo estricto (sin pausa)</span>}>
            <Switch on={settings.strictMode} onChange={(v) => set({ strictMode: v })} />
          </Row>
          <Row label="Avisar X min antes"><Stepper value={settings.notifyMinutesBefore} min={0} max={5} onChange={(v) => set({ notifyMinutesBefore: v })} /></Row>
        </div>

        <SectionLabel>Notificaciones</SectionLabel>
        <div className="card" style={{ borderRadius: 13, overflow: 'hidden' }}>
          <Row first label={<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Bell size={15} color="var(--text-mut)" /> Notificaciones del navegador</span>}>
            {notifPerm === 'granted' ? <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>Activadas</span>
              : notifPerm === 'unsupported' ? <span style={{ fontSize: 13, color: 'var(--text-mut)' }}>No disponible</span>
              : <button className="btn-primary" style={{ padding: '7px 14px', fontSize: 13 }} onClick={enableNotifs}>Activar</button>}
          </Row>
        </div>

        <SectionLabel>Sonido</SectionLabel>
        <div className="card" style={{ borderRadius: 13, overflow: 'hidden' }}>
          <button style={rowBtn} onClick={() => setShowNoise(true)}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}><Volume2 size={15} color="var(--text-mut)" /> Sonidos de fondo</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-mut)', fontSize: 13 }}>
              {settings.whiteNoise.enabled ? `${Object.values(settings.whiteNoise.sounds).filter((v) => v > 0).length} activos` : 'Off'} <ChevronRight size={15} />
            </span>
          </button>
          <Row label="Tic-tac de reloj"><Switch on={settings.tickingEnabled} onChange={(v) => set({ tickingEnabled: v })} /></Row>
          <Row label="Volumen alarma">
            <input type="range" className="ios" min={0} max={100} value={Math.round(settings.alarmVolume * 100)} onChange={(e) => set({ alarmVolume: Number(e.target.value) / 100 })} style={{ width: 120, accentColor: 'var(--primary)' }} />
          </Row>
        </div>

        <SectionLabel>Bloqueo de webs</SectionLabel>
        <div className="card" style={{ borderRadius: 13, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <ShieldBan size={17} color="var(--text-mut)" />
            <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>Bloquear webs que distraen</span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: extConnected ? 'var(--green)' : 'var(--text-mut)' }}>
              {extConnected ? 'Extensión conectada' : 'Requiere extensión'}
            </span>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-mut)', margin: 0, lineHeight: 1.5 }}>
            {extConnected
              ? 'La extensión de FlowFocus está activa: cuando inicies un enfoque aquí, bloqueará automáticamente las webs de tu lista. Edita la lista desde el popup de la extensión.'
              : 'Una página web no puede bloquear otras webs (lo impide el navegador por seguridad). Para bloquear durante el enfoque, instala la extensión de FlowFocus en este navegador: entonces los enfoques que inicies aquí también activarán el bloqueo.'}
          </p>
        </div>

        <SectionLabel>Apariencia</SectionLabel>
        <Segmented value={settings.darkMode} options={[{ id: 'light', label: 'Claro' }, { id: 'dark', label: 'Oscuro' }, { id: 'system', label: 'Sistema' }]} onChange={(v) => set({ darkMode: v })} />

        <p style={{ textAlign: 'center', color: 'var(--text-mut)', fontSize: 11, marginTop: 26 }}>Atajos: espacio = play/pausa · F = pantalla completa · S = saltar · R = reiniciar</p>
      </div>

      {showNoise && <Sheet title="Sonidos de fondo" onClose={() => setShowNoise(false)}><WhiteNoisePanel /></Sheet>}
    </div>
  );
}

function Row({ label, children, first }: { label: React.ReactNode; children: React.ReactNode; first?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 15px', borderTop: first ? 'none' : '0.5px solid var(--border)' }}>
      <span style={{ fontSize: 14 }}>{label}</span>
      {children}
    </div>
  );
}
const rowBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 15px', width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' };
